import { Close } from "@material-ui/icons";
import {
  AccessTime as AccessTimeIcon,
  CalendarMonth as CalendarMonthIcon,
  Description as DescriptionIcon,
  DirectionsCar as DirectionsCarIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  Leaderboard as LeaderboardIcon,
  LocationOn as LocationOnIcon,
  Spa as SpaIcon,
  Storefront as StorefrontIcon,
  Timeline as TimelineIcon,
  Today as TodayIcon,
  VerifiedUser as VerifiedUserIcon,
} from "@mui/icons-material";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { API_URL } from "app/config";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import AnalyticsCard, { TrendItem } from "./AnalyticsCard";
import AnalyticsTimeSeries from "./AnalyticsTimeSeries";
import { ProductStatusCards } from "features/dashboard/CrmModules/ProductStatsCard";

const InsuranceSection = ({
  analyticsData,
  getAllAnalyticsData,
  mainStateName,
  dateRange,
  formatDate,productKey
}) => {
  const insurance = analyticsData?.data?.insurance;
  const insuranceSummary = insurance?.summary;
  const topInsuranceStates = insurance?.top_states;
  const topInsuranceAgents = insurance?.top_agents;
  const topInsuranceDistricts = insurance?.top_districts || [];
  const insuranceTimeSeries = insurance?.timeseries || [];
  const [selectedInsuranceState, setSelectedInsuranceState] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStateDistricts, setSelectedStateDistricts] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleStateClick = async (stateName) => {
    setSelectedInsuranceState(stateName);
    setOpenDialog(true);
    setSelectedStateDistricts([]);
    setLoading(true);

    try {
      const [from, to] = dateRange || [];
      const payload = {
        state_name: stateName,
        ...(from ? { from_date: formatDate(from) } : {}),
        ...(to ? { to_date: formatDate(to) } : {}),
      };
      const response = await fetch(
        `${API_URL}/mis/api/v1/mis-analytics?use_cache=true`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();
      const districts = data?.data?.insurance?.top_districts;

      setSelectedStateDistricts(districts);
    } catch (error) {
      console.error("Error fetching district data:", error);
      setSelectedStateDistricts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mainStateName) {
      setSelectedInsuranceState(mainStateName);
    } else {
      setSelectedInsuranceState(null);
    }
  }, [mainStateName]);

  useEffect(() => {
    if (mainStateName) {
      const [from, to] = dateRange || [];
      const payload = {
        state_name: mainStateName,
        ...(from ? { from_date: formatDate(from) } : {}),
        ...(to ? { to_date: formatDate(to) } : {}),
      };

      getAllAnalyticsData(payload);
    }
  }, [mainStateName, dateRange, getAllAnalyticsData]);

  return (
    <Box>
       {!productKey?
      <Grid container spacing={2} marginBottom={3}>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Total Leads"
            count={insuranceSummary?.total_leads}
            icon={DescriptionIcon}
            color="#0ea5e9"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Crop Insurance Leads"
            count={insuranceSummary?.total_crop_insurance_leads}
            icon={SpaIcon}
            color="#6366f1"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Cattle Insurance"
            count={insuranceSummary?.total_cattle_insurance_leads}
            icon={VerifiedUserIcon}
            color="#22c55e"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Motor Insurance"
            count={insuranceSummary?.total_motor_insurance_leads}
            icon={DirectionsCarIcon}
            color="#f59e0b"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Health Insurance"
            count={insuranceSummary?.total_health_insurance_leads}
            icon={HealthAndSafetyIcon}
            color="#f59e0b"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Shopkeeper Insurance"
            count={insuranceSummary?.total_shopkeeper_insurance_leads}
            icon={StorefrontIcon}
            color="#ef4444"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <AnalyticsCard
            title="Leads Summary"
            icon={TimelineIcon}
            color="#18A2DE"
          >
            <div
              className="d-flex justify-content-between"
              style={{ marginTop: "-4.5px" }}
            >
              <div className="d-flex flex-column gap-2">
                <TrendItem
                  label="Today"
                  value={insuranceSummary?.total_leads_generated_today}
                  icon={TodayIcon}
                  color="#0ea5e9"
                />
                <TrendItem
                  label="Last 7 Days"
                  value={insuranceSummary?.total_leads_generated_last_7_days}
                  icon={AccessTimeIcon}
                  color="#6366f1"
                />
              </div>
              <div className="d-flex flex-column gap-2">
                <TrendItem
                  label="Last 1 Month"
                  value={insuranceSummary?.total_leads_generated_last_1_month}
                  icon={CalendarMonthIcon}
                  color="#f59e0b"
                />

                <TrendItem
                  label="Last 1 Year"
                  value={insuranceSummary?.total_leads_generated_last_1_year}
                  icon={TimelineIcon}
                  color="#22c55e"
                />
              </div>
            </div>
          </AnalyticsCard>
        </Grid>
      </Grid>
      :<ProductStatusCards productKey={productKey?.toLowerCase()} insurance={insurance} statusData={insurance?.top_current_status}/>}
    
      <Grid container spacing={2} marginBottom={3}>
        <Grid item xs={12} sm={6} md={productKey?6:4}>
          <AnalyticsCard
            title={
              mainStateName
                ? `Districts in ${mainStateName}`
                : "Top Performing States"
            }
            icon={LocationOnIcon}
            color="#14b8a6"
            sx={{ height: "100%", position: "relative" }}
          >
            <Box
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mt: -2,
                mb: -3.5,
              }}
            >
              {mainStateName
                ? topInsuranceDistricts?.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        borderBottom:
                          index !== topInsuranceDistricts.length - 1
                            ? "1px solid #f0f0f0"
                            : "none",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "#333", width: "80%" }}
                      >
                        {item.district_name || "Other"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#0ea5e9",
                          textAlign: "right",
                        }}
                      >
                        {item.total_count}
                      </Typography>
                    </Box>
                  ))
                : topInsuranceStates?.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        borderBottom:
                          index !== topInsuranceStates.length - 1
                            ? "1px solid #f0f0f0"
                            : "none",
                        cursor: !mainStateName ? "pointer" : "default",
                        "&:hover": !mainStateName
                          ? { background: "#f9fafb" }
                          : {},
                      }}
                      onClick={() => handleStateClick(item.state_name)}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "#333",
                          width: "80%",
                        }}
                      >
                        {mainStateName
                          ? item.district_name
                          : item.state_name || "Other"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#14b8a6",
                          textAlign: "right",
                        }}
                      >
                        {item.total_count}
                      </Typography>
                    </Box>
                  ))}
            </Box>
          </AnalyticsCard>
        </Grid>

        {/*  Top Insurance Agents */}
        <Grid item xs={12} sm={6} md={ productKey?6:4}>
          <AnalyticsCard
            title="Top Performing Agents"
            icon={LeaderboardIcon}
            color="#22c55e"
            sx={{ height: "100%" }}
          >
            <Box
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mt: -2,
                mb: -3.5,
              }}
            >
              {topInsuranceAgents?.map((agent, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    borderBottom:
                      index !== topInsuranceAgents.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#333", width: "80%" }}
                  >
                    {agent.agent_name || "Other"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#22c55e",
                      textAlign: "right",
                    }}
                  >
                    {agent.total_count}
                  </Typography>
                </Box>
              ))}
            </Box>
          </AnalyticsCard>
        </Grid>
          {!productKey&&
        <Grid item xs={12} sm={6} md={4}>
          <AnalyticsCard
            title="Top Performing Products"
            icon={StorefrontIcon}
            color="#f59e0b"
            sx={{ height: "100%" }}
          >
            <Box
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mt: -2,
                mb: -3.5,
              }}
            >
              {[
                {
                  label: "Crop Insurance",
                  value: insuranceSummary?.total_crop_insurance_leads,
                  // color: "#0ea5e9",
                  color: "#f59e0b",
                },
                {
                  label: "Cattle Insurance",
                  value: insuranceSummary?.total_cattle_insurance_leads,
                  // color: "#6366f1",
                  color: "#f59e0b",
                },
                {
                  label: "Motor Insurance",
                  value: insuranceSummary?.total_motor_insurance_leads,
                  color: "#f59e0b",
                },
                {
                  label: "Health Insurance",
                  value: insuranceSummary?.total_health_insurance_leads,
                  // color: "#8b5cf6",
                  color: "#f59e0b",
                },
                {
                  label: "Shopkeeper Insurance",
                  value: insuranceSummary?.total_shopkeeper_insurance_leads,
                  // color: "#22c55e",
                  color: "#f59e0b",
                },
              ]
                .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
                .map((item, index, arr) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom:
                        index !== arr.length - 1 ? "1px solid #f0f0f0" : "none",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: "#333", width: "80%" }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: item.color,
                        textAlign: "right",
                      }}
                    >
                      {item.value ?? 0}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </AnalyticsCard>
        </Grid>
        }
      </Grid>

      <Grid container spacing={2} marginBottom={3}>
        <Grid item xs={12}>
          <AnalyticsTimeSeries
            TimeSeries={insuranceTimeSeries}
            title="Leads Trend"
          />
        </Grid>
      </Grid>

      <Modal
        show={openDialog}
        onHide={() => setOpenDialog(false)}
        size="md"
        className="analytics-modal "
      >
        <AnalyticsCard
          title={` Districts in ${selectedInsuranceState}`}
          // icon={LocationOnIcon}
          color="#22c55e"
          sx={{
            marginBottom: 0,
            "&:hover": {
              transform: "translateY(0px)",
            },
          }}
        >
          <Close
            style={{
              position: "absolute",
              top: 10,
              right: 16,
              fontSize: 22,
              opacity: 0.8,
              color: "#22c55e",
            }}
            onClick={() => setOpenDialog(false)}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "100px",
                }}
              >
                <CircularProgress />
              </Box>
            ) : selectedStateDistricts?.length > 0 ? (
              selectedStateDistricts.map((item, index) => (
                <>
                  <Box
                    key={index}
                    className="px-2"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom:
                        index !== selectedStateDistricts.length - 1
                          ? "1px solid #f0f0f0"
                          : "none",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: "#333", width: "80%" }}
                    >
                      {item.district_name || "Other"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "#0ea5e9",
                        textAlign: "right",
                      }}
                    >
                      {item.total_count}
                    </Typography>
                  </Box>
                </>
              ))
            ) : (
              <Typography textAlign="center" color="text.secondary">
                No districts found.
              </Typography>
            )}
          </Box>
        </AnalyticsCard>
      </Modal>
    </Box>
  );
};

export default InsuranceSection;
